import type { ReactNode } from 'react';

export type ShellStyle = 'floating' | 'flush' | 'hybrid';
export type ShellPreset = 'writing' | 'reading' | 'focus' | 'research' | 'review' | 'immersive';
export type TopBarTier = 'rich' | 'standard' | 'compact' | 'hidden';
export type SidebarPosition = 'left' | 'right' | 'collapsed' | 'hidden';
export type StatusBarVisibility = 'visible' | 'hidden';
export type PageWidth = 'narrow' | 'normal' | 'wide' | 'full';
export type PageMargin = 'tight' | 'normal' | 'generous';
export type PageColumns = 1 | 2;
export type PageLineHeight = 'compact' | 'standard' | 'relaxed';

/** Personalization axes that drive AppShell geometry + visual style.
 *  Field names are prefixed (`sidebarPosition`, `rightPanelPosition`,
 *  `statusBarVisibility`) to avoid collisions with the ReactNode slot
 *  props on AppShell (`sidebar`, `inspector`, `statusBar`). */
export interface ShellAxes {
  /** 视觉风格：浮动卡片 / 贴边 / 桌面-floating+移动-flush 混合。Default `'floating'`. */
  shellStyle?: ShellStyle;
  /** Gap between floating panels, px. Default 12. */
  shellGap?: number;
  /** Top-bar density. Default `'standard'`. */
  topBar?: TopBarTier;
  /** Sidebar position + visibility. Default `'left'`. */
  sidebarPosition?: SidebarPosition;
  /** Sidebar width, px. Default 260. */
  sidebarWidth?: number;
  /** Right inspector panel. Default `'right'`. */
  rightPanelPosition?: SidebarPosition;
  /** Right panel width, px. Default 280. */
  rightPanelWidth?: number;
  /** Status bar. Default `'visible'`. */
  statusBarVisibility?: StatusBarVisibility;
  /** Document page width. Default `'normal'`. */
  pageWidth?: PageWidth;
  /** Document page margin density. Default `'normal'`. */
  pageMargin?: PageMargin;
  /** Document page columns. Default 1. */
  pageColumns?: PageColumns;
  /** Document page line height. Default `'standard'`. */
  pageLineHeight?: PageLineHeight;
}

export interface AppShellProps extends ShellAxes {
  /** Top-left topbar island. Usually brand + hamburger. */
  topLeft?: ReactNode;
  /** Top-center topbar island. Usually breadcrumb / location anchor. */
  topCenter?: ReactNode;
  /** Top-right topbar island. Usually controls / axis selects / user. */
  topRight?: ReactNode;
  /** Left rail panel content. */
  sidebar?: ReactNode;
  /** Main story / document canvas. The shell focuses here on mount. */
  main: ReactNode;
  /** Right rail panel (inspector, comments, properties). */
  inspector?: ReactNode;
  /** Bottom status bar island (e.g., page x/y · word count · zoom). */
  statusBar?: ReactNode;
  /** Label the main landmark for screen readers. */
  mainLabel?: string;
  /** id of the `<main>` element — lets `SkipLink` target it. Default `vq-main`. */
  mainId?: string;
  /** Forwarded root className for shell-level overrides. */
  className?: string;
  /** Mobile drawer open state for the sidebar. */
  sidebarDrawerOpen?: boolean;
  /** Fires when the drawer scrim is clicked or Esc pressed. */
  onSidebarDrawerClose?: () => void;
  /** Apply a named preset (spec 09 §4) — sets multiple axes in one shot.
   *  Individual axes still override preset defaults. */
  preset?: ShellPreset;
}

/** Preset → axis defaults, per spec 09 §4. Axes unspecified by a preset
 *  inherit the shell defaults; explicit props on AppShell always win. */
export const PRESET_AXES: Record<ShellPreset, ShellAxes> = {
  writing: {
    shellStyle: 'floating',
    topBar: 'standard',
    sidebarPosition: 'left',
    rightPanelPosition: 'right',
    statusBarVisibility: 'visible',
    pageWidth: 'normal',
    pageMargin: 'normal',
  },
  reading: {
    shellStyle: 'flush',
    topBar: 'compact',
    sidebarPosition: 'collapsed',
    rightPanelPosition: 'collapsed',
    statusBarVisibility: 'hidden',
    pageWidth: 'wide',
    pageMargin: 'generous',
    pageLineHeight: 'relaxed',
  },
  focus: {
    shellStyle: 'floating',
    topBar: 'hidden',
    sidebarPosition: 'hidden',
    rightPanelPosition: 'hidden',
    statusBarVisibility: 'hidden',
    pageWidth: 'normal',
    pageMargin: 'generous',
  },
  research: {
    shellStyle: 'floating',
    topBar: 'rich',
    sidebarPosition: 'left',
    rightPanelPosition: 'right',
    statusBarVisibility: 'visible',
    pageWidth: 'wide',
    pageMargin: 'normal',
    pageColumns: 2,
  },
  review: {
    shellStyle: 'flush',
    topBar: 'standard',
    sidebarPosition: 'left',
    rightPanelPosition: 'right',
    statusBarVisibility: 'visible',
    pageWidth: 'normal',
    pageMargin: 'tight',
  },
  immersive: {
    shellStyle: 'flush',
    topBar: 'hidden',
    sidebarPosition: 'hidden',
    rightPanelPosition: 'hidden',
    statusBarVisibility: 'hidden',
    pageWidth: 'full',
    pageMargin: 'tight',
  },
};
